// IndexedDB-backed queue for offline operator actions.
//
// Supports multiple payload kinds — each item has a `kind` string that
// determines how it's drained:
//
//   photo_upload   { blob, filename, contentType, kind:"photo_upload",
//                    formKind } → POST /api/photos/upload
//   job_photo      { blob, filename, contentType, jobId, assetId,
//                    moment, checklistKey, note } → POST /api/jobs/{jobId}/photos
//   meter_reading  { blob?, filename?, contentType?, assetId, jobId,
//                    kindOfMeter, value } → POST /api/meter-readings
//   fuel_log       { blob?, filename?, contentType?, assetId, jobId,
//                    date, liters, total_cost_mxn,
//                    hour_meter_in?, hour_meter_out?,
//                    odometer_in_km?, odometer_out_km? } → POST /api/fuel-logs
//   viaje          { jobId, body:{asset_id, material_id, quantity, origin_text, destination_text, notes} }
//                  → POST /api/jobs/{jobId}/viajes
//   idle_toggle    { jobId, body:{idle, reason} } → POST /api/jobs/{jobId}/idle
//   checkin        { body:{note} } → POST /api/attendance/check-in
//   checkout       { body:{note} } → POST /api/attendance/check-out
//   toll_create    { blob?, filename?, contentType?, jobId, assetId,
//                    date, location, amount_mxn, notes? } → (optional)
//                  POST /api/photos/upload + POST /api/tolls
//
// Each row also has { id, queuedAt }. Items are drained FIFO.

import { api } from "./api";

const DB_NAME = "flotilla_mx";
const DB_VERSION = 1;
const STORE = "offline_uploads";

function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: "id" });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function tx(db, mode) {
    return db.transaction(STORE, mode).objectStore(STORE);
}

function genId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

async function _add(row) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const req = tx(db, "readwrite").add(row);
        req.onsuccess = () => resolve(row.id);
        req.onerror = () => reject(req.error);
    });
}

// Back-compat: original signature for photo uploads on /campo/camara.
export async function enqueueUpload({ blob, filename, contentType, kind }) {
    const id = genId();
    return _add({
        id, kind: "photo_upload",
        blob, filename, contentType, formKind: kind,
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueJobPhoto({ blob, filename, contentType, jobId, assetId, moment, checklistKey, note }) {
    return _add({
        id: genId(), kind: "job_photo",
        blob, filename, contentType, jobId, assetId, moment, checklistKey, note,
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueMeterReading({ blob, filename, contentType, assetId, jobId, kindOfMeter, value }) {
    return _add({
        id: genId(), kind: "meter_reading",
        blob, filename, contentType, assetId, jobId, kindOfMeter, value,
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueFuelLog(payload) {
    return _add({
        id: genId(), kind: "fuel_log",
        ...payload,
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueViaje({ jobId, body }) {
    return _add({
        id: genId(), kind: "viaje", jobId, body,
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueIdleToggle({ jobId, body }) {
    return _add({
        id: genId(), kind: "idle_toggle", jobId, body,
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueCheckIn({ body }) {
    return _add({
        id: genId(), kind: "checkin", body: body || {},
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueCheckOut({ body }) {
    return _add({
        id: genId(), kind: "checkout", body: body || {},
        queuedAt: new Date().toISOString(),
    });
}

export async function enqueueTollCreate({ blob, filename, contentType, jobId, assetId, date, location, amount_mxn, notes }) {
    return _add({
        id: genId(), kind: "toll_create",
        blob: blob || null, filename: filename || null, contentType: contentType || null,
        jobId, assetId: assetId || null, date, location, amount_mxn, notes: notes || null,
        queuedAt: new Date().toISOString(),
    });
}

export async function listQueued() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const req = tx(db, "readonly").getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

export async function removeQueued(id) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const req = tx(db, "readwrite").delete(id);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

export async function countQueued() {
    const items = await listQueued();
    return items.length;
}

// Cross-tab pending count broadcast
const PENDING_EVENT = "flotilla:pending-changed";

export async function broadcastPending() {
    const n = await countQueued();
    window.dispatchEvent(new CustomEvent(PENDING_EVENT, { detail: { count: n } }));
    return n;
}

export function onPendingChanged(handler) {
    const fn = (e) => handler(e?.detail?.count ?? 0);
    window.addEventListener(PENDING_EVENT, fn);
    return () => window.removeEventListener(PENDING_EVENT, fn);
}

// ---------------------------------------------------------------------------
// Drain — replay queued items against the API in FIFO order.
// Stops on first network failure to preserve ordering.
// ---------------------------------------------------------------------------

async function _replayPhotoUpload(it) {
    const fd = new FormData();
    fd.append("file", it.blob, it.filename);
    fd.append("kind", it.formKind || "operator_field");
    await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
}

async function _replayJobPhoto(it) {
    const fd = new FormData();
    fd.append("file", it.blob, it.filename || "photo.jpg");
    fd.append("asset_id", it.assetId);
    fd.append("moment", it.moment);
    fd.append("checklist_key", it.checklistKey);
    if (it.note) fd.append("note", it.note);
    await api.post(`/jobs/${it.jobId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
}

async function _replayMeterReading(it) {
    const fd = new FormData();
    if (it.blob) fd.append("file", it.blob, it.filename || "meter.jpg");
    fd.append("asset_id", it.assetId);
    fd.append("job_id", it.jobId);
    fd.append("kind", it.kindOfMeter);
    fd.append("value", String(it.value));
    await api.post("/meter-readings", fd, { headers: { "Content-Type": "multipart/form-data" } });
}

async function _replayFuelLog(it) {
    const fd = new FormData();
    if (it.blob) fd.append("file", it.blob, it.filename || "ticket.jpg");
    fd.append("asset_id", it.assetId);
    fd.append("job_id", it.jobId);
    fd.append("date", it.date);
    fd.append("liters", String(it.liters));
    fd.append("total_cost_mxn", String(it.total_cost_mxn));
    if (it.hour_meter_in != null) fd.append("hour_meter_in", String(it.hour_meter_in));
    if (it.hour_meter_out != null) fd.append("hour_meter_out", String(it.hour_meter_out));
    if (it.odometer_in_km != null) fd.append("odometer_in_km", String(it.odometer_in_km));
    if (it.odometer_out_km != null) fd.append("odometer_out_km", String(it.odometer_out_km));
    await api.post("/fuel-logs", fd, { headers: { "Content-Type": "multipart/form-data" } });
}

async function _replayViaje(it) {
    await api.post(`/jobs/${it.jobId}/viajes`, it.body);
}

async function _replayIdle(it) {
    await api.post(`/jobs/${it.jobId}/idle`, it.body);
}

async function _replayCheckIn(it) { await api.post("/attendance/check-in", it.body || {}); }
async function _replayCheckOut(it) { await api.post("/attendance/check-out", it.body || {}); }

async function _replayTollCreate(it) {
    let photo_id = null;
    if (it.blob) {
        const fd = new FormData();
        fd.append("file", it.blob, it.filename || "toll.jpg");
        fd.append("kind", "toll_receipt");
        const r = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_id = r.data?.id || null;
    }
    await api.post("/tolls", {
        job_id: it.jobId,
        asset_id: it.assetId || null,
        date: it.date,
        location: it.location,
        amount_mxn: it.amount_mxn,
        photo_id,
        notes: it.notes || null,
    });
}

const HANDLERS = {
    photo_upload: _replayPhotoUpload,
    job_photo: _replayJobPhoto,
    meter_reading: _replayMeterReading,
    fuel_log: _replayFuelLog,
    viaje: _replayViaje,
    idle_toggle: _replayIdle,
    checkin: _replayCheckIn,
    checkout: _replayCheckOut,
    toll_create: _replayTollCreate,
};

// Drains the queue. Returns { synced, dropped, total } counts.
// Items that fail with 4xx (validation, stale data) are dropped — they
// can't succeed without operator intervention; we don't want to block the
// queue forever. Items that fail with no response (network error) stop the
// drain and leave the queue intact.
export async function drainQueue() {
    const items = (await listQueued()).slice().sort((a, b) => (a.queuedAt < b.queuedAt ? -1 : 1));
    let synced = 0, dropped = 0;
    for (const it of items) {
        const fn = HANDLERS[it.kind];
        if (!fn) { await removeQueued(it.id); dropped += 1; continue; }
        try {
            await fn(it);
            await removeQueued(it.id);
            synced += 1;
        } catch (err) {
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500) {
                // permanent client error — drop & log
                console.warn("[offlineQueue] dropping item", it.id, it.kind, status, err?.response?.data?.detail);
                await removeQueued(it.id);
                dropped += 1;
                continue;
            }
            // network or server error — stop draining to preserve order
            break;
        }
    }
    await broadcastPending();
    return { synced, dropped, total: items.length };
}
